import { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { X, Globe, AlertCircle, Check, Loader2, Plus, Trash2, CornerDownRight } from 'lucide-react'
import { useBuilder } from './hooks/useBuilder'
import { useScrimDismiss } from '../shared/hooks/useScrimDismiss'
import { useEscapeClose } from '../shared/hooks/useEscapeClose'
import { useBuilderSites } from './hooks/useBuilderSites'

/**
 * Panneau « Adresse » — l'URI publique de la page, et le site auquel elle
 * appartient.
 *
 * Deux réglages, une même question : « où cette page est-elle servie ? ».
 *
 *  - L'URI (le `slug`) est une DONNÉE de la page, pas son identité : le save
 *    envoie le `nid`, donc changer l'URI renomme la page au lieu d'en créer
 *    une seconde. La disponibilité est tranchée par Drupal
 *    (`/api/builder/slug-check`), jamais ici : le front ne connaît ni les
 *    pages qu'il ne voit pas, ni les slugs réservés aux routes de l'appli.
 *    Le même contrôle refuse la sauvegarde (409) si l'URI est prise entre
 *    la vérification et le Save.
 *
 *  - Le site est un GROUPE Drupal (`group_type: site`, cf. module orbit_site) :
 *    le rattachement est une relation de groupe, pas un champ. La liste vient
 *    de l'API générique decoupled — donc déjà filtrée par les droits : on ne
 *    peut rattacher qu'à un site qu'on a le droit de voir.
 *
 *  - Les ANCIENNES ADRESSES (`field_redirects`) sont, elles aussi, de la
 *    donnée de page : un chemin qui n'est plus servi mais qui doit continuer
 *    de mener ici. Le renommage en pose une tout seul — c'est le serveur qui
 *    le fait, au save, là où l'ancien slug est encore connu. Une case, sous
 *    l'URI, permet de la REFUSER (`redirectOnRename: false` dans le payload du
 *    save) : sur une page jamais publiée, la reprise ne ferait qu'occuper un
 *    chemin que le contrôle d'unicité refuserait ensuite à une autre page.
 *
 * Comme le panneau « Modèles », rien n'est écrit ici : « Appliquer » pose les
 * valeurs dans l'état de la page et arme le `isDirty` ; le Save du header les
 * persiste (et la barre navigue vers la nouvelle URI).
 */
export const BuilderPageAddressPanel = ({ onClose }) => {
  const scrim = useScrimDismiss(onClose)
  const { page, setPage, setIsDirty } = useBuilder()

  const [uri, setUri] = useState(page.slug || '')
  const [siteId, setSiteId] = useState(page.siteId ?? '')
  // Lu, plus jamais écrit ici : un gabarit naît comme tel (« Nouveau gabarit »
  // depuis l'écran du site), une page n'en devient pas un en cochant une case.
  const isTemplate = page.isTemplate === true
  const initialRedirects = Array.isArray(page.redirects) ? page.redirects : []
  const [redirects, setRedirects] = useState(initialRedirects)
  const [newPath, setNewPath] = useState('')
  // Le renommage pose une redirection depuis l'ancienne adresse — c'est le
  // serveur qui le fait, au save. On peut le REFUSER ici : une page qu'on vient
  // de créer et dont on corrige l'URI n'a jamais été servie, et sa redirection
  // ne ferait qu'occuper un chemin (le back interdit ensuite de le réutiliser
  // comme slug). Lu depuis l'état de la page : un « Appliquer » précédent, non
  // encore enregistré, garde son choix.
  const [keepOldAddress, setKeepOldAddress] = useState(page.redirectOnRename !== false)
  // { state: 'idle' | 'checking' | 'ok' | 'ko', message }
  const [pathCheck, setPathCheck] = useState({ state: 'idle' })
  // Les sites visibles par l'utilisateur — même source, désormais partagée avec
  // la création d'un type de contenu (cf. useBuilderSites).
  const { sites, loading: sitesLoading, error: sitesError } = useBuilderSites()
  // { state: 'idle' | 'checking' | 'ok' | 'ko', reason }
  const [check, setCheck] = useState({ state: 'idle' })
  const inputRef = useRef(null)

  useEffect(() => { inputRef.current?.focus() }, [])

  useEscapeClose(onClose)


  // Disponibilité de l'URI — tranchée côté Drupal, en différé pendant la frappe.
  useEffect(() => {
    const trimmed = uri.trim()
    if (trimmed === '' || trimmed === (page.slug || '')) {
      setCheck({ state: 'idle' })
      return
    }
    setCheck({ state: 'checking' })
    let cancelled = false
    const timer = setTimeout(async () => {
      try {
        const params = new URLSearchParams({ slug: trimmed })
        if (page.pageNid) params.set('nid', String(page.pageNid))
        const res = await fetch(`/api/builder/slug-check?${params}`, { credentials: 'include' })
        if (!res.ok) throw new Error(String(res.status))
        const data = await res.json()
        if (cancelled) return
        setCheck(data.available ? { state: 'ok' } : { state: 'ko', reason: data.reason })
      } catch {
        // Vérification indisponible : on n'invente pas un verdict — la
        // sauvegarde refusera l'URI si elle est prise.
        if (!cancelled) setCheck({ state: 'idle' })
      }
    }, 400)
    return () => { cancelled = true; clearTimeout(timer) }
  }, [uri, page.slug, page.pageNid])

  // Disponibilité d'une ancienne adresse — même mécanique que l'URI ci-dessus,
  // et même règle : le verdict vient de Drupal, avec son message. Il tient
  // compte du SITE de la page, donc deux sites peuvent revendiquer /contact
  // chacun de leur côté sans se gêner.
  useEffect(() => {
    const trimmed = newPath.trim()
    if (trimmed === '') {
      setPathCheck({ state: 'idle' })
      return
    }
    // Une adresse déjà dans la liste en cours d'édition : le serveur ne la voit
    // pas encore (rien n'est enregistré avant le Save), c'est donc au panneau de
    // le dire.
    if (redirects.some(r => r.from === normalizePath(trimmed))) {
      setPathCheck({ state: 'ko', message: 'Cette adresse est déjà dans la liste.' })
      return
    }
    setPathCheck({ state: 'checking' })
    let cancelled = false
    const timer = setTimeout(async () => {
      try {
        const params = new URLSearchParams({ path: normalizePath(trimmed) })
        if (page.pageNid) params.set('nid', String(page.pageNid))
        const res = await fetch(`/api/builder/redirect-check?${params}`, { credentials: 'include' })
        if (!res.ok) throw new Error(String(res.status))
        const data = await res.json()
        if (cancelled) return
        setPathCheck(data.available ? { state: 'ok' } : { state: 'ko', message: data.message })
      } catch {
        // Vérification indisponible : on n'invente pas un verdict — la
        // sauvegarde refusera l'adresse si elle est prise.
        if (!cancelled) setPathCheck({ state: 'idle' })
      }
    }, 400)
    return () => { cancelled = true; clearTimeout(timer) }
  }, [newPath, page.pageNid, redirects])

  const addRedirect = () => {
    const path = normalizePath(newPath)
    if (pathCheck.state !== 'ok' || path === '/') return
    setRedirects(prev => [...prev, { from: path, code: 301 }])
    setNewPath('')
    setPathCheck({ state: 'idle' })
  }
  const removeRedirect = from => setRedirects(prev => prev.filter(r => r.from !== from))
  const setRedirectCode = (from, code) =>
    setRedirects(prev => prev.map(r => (r.from === from ? { ...r, code } : r)))

  const uriChanged = uri.trim() !== (page.slug || '')
  const siteChanged = (siteId === '' ? null : Number(siteId)) !== (page.siteId ?? null)

  const redirectsChanged = JSON.stringify(redirects) !== JSON.stringify(initialRedirects)
  const keepChanged = keepOldAddress !== (page.redirectOnRename !== false)
  const canApply = uri.trim() !== '' && check.state !== 'ko' && check.state !== 'checking'
    && (uriChanged || siteChanged || redirectsChanged || keepChanged)

  const handleApply = () => {
    if (!canApply) return
    const nextSite = siteId === '' ? null : Number(siteId)
    setPage(prev => ({
      ...prev,
      slug: uri.trim(),
      siteId: nextSite,
      siteName: sites.find(s => s.id === nextSite)?.name ?? '',
      redirects,
      // Intention pour CE save, pas un réglage de la page : `useBuilderSave`
      // n'envoie la clé que lorsqu'elle vaut `false` et la remet à `true` une
      // fois la page enregistrée.
      redirectOnRename: keepOldAddress,
    }))
    setIsDirty(true)
    onClose()
  }

  // Portail vers <body> + z-index au-dessus du contenu de bloc : un bloc du
  // canevas s'empile haut (une navbar de site en `z-[100]`, l'Assistant en
  // `z-[200]`) et TRANSPERÇAIT ce panneau rendu en place au z-50 du DS — son
  // en-tête et l'intitulé de l'URI disparaissaient sous le header du site.
  // Même parade que le catalogue de blocs et la médiathèque.
  return createPortal(
    <div className="fixed inset-0 z-[300] flex items-center justify-center bg-gray-900/50 p-4" {...scrim}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="builder-address-title"
        className="bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[90vh] flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        <header className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Globe size={16} className="text-teal-700" />
            <h2 id="builder-address-title" className="text-base font-semibold text-gray-800">Adresse de la page</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors" aria-label="Fermer">
            <X size={18} />
          </button>
        </header>

        <div className="px-6 py-5 space-y-5 overflow-y-auto">
          {/* URI */}
          <div>
            <label htmlFor="builder-uri" className="block text-xs font-medium text-gray-500 mb-1.5">
              URI publique
            </label>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-400 font-mono">/</span>
              <input
                id="builder-uri"
                ref={inputRef}
                type="text"
                value={uri}
                onChange={e => setUri(sanitizeUri(e.target.value))}
                aria-describedby="builder-uri-help builder-uri-status"
                aria-invalid={check.state === 'ko'}
                className={`flex-1 px-3 py-2 text-sm font-mono border rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-600 ${
                  check.state === 'ko' ? 'border-red-300' : 'border-gray-200'
                }`}
                placeholder="ma-page"
              />
            </div>
            <p id="builder-uri-help" className="text-xs text-gray-400 mt-1.5">
              Minuscules, chiffres et tirets.{' '}
              {isTemplate
                ? "Un gabarit n'est pas servi publiquement : cette URI n'est que son adresse d'édition."
                : <>La page sera servie à <span className="font-mono">/{uri.trim() || 'ma-page'}</span>.</>}
            </p>
            {/* aria-live : le verdict arrive après la frappe, il doit être annoncé. */}
            <p id="builder-uri-status" role="status" aria-live="polite" className="mt-1.5 text-xs">
              {check.state === 'checking' && (
                <span className="flex items-center gap-1.5 text-gray-400">
                  <Loader2 size={12} className="animate-spin" /> Vérification…
                </span>
              )}
              {check.state === 'ok' && (
                <span className="flex items-center gap-1.5 text-teal-700">
                  <Check size={12} /> URI disponible.
                </span>
              )}
              {check.state === 'ko' && (
                <span className="flex items-center gap-1.5 text-red-600">
                  <AlertCircle size={12} /> {URI_ERRORS[check.reason] || 'URI indisponible.'}
                </span>
              )}
            </p>
            {/* Le renommage pose une redirection tout seul : on l'ANNONCE, et on
                laisse la refuser. Sans ce choix, corriger l'URI d'une page jamais
                publiée laissait derrière elle un chemin mort — que le back refuse
                ensuite de rendre à une autre page tant qu'il n'est pas retiré. */}
            {uriChanged && page.slug && (
              <div className={`mt-2 text-xs rounded-lg px-3 py-2 border ${
                keepOldAddress
                  ? 'text-teal-800 bg-teal-50 border-teal-100'
                  : 'text-amber-800 bg-amber-50 border-amber-100'
              }`}>
                <p className="flex items-start gap-1.5">
                  <CornerDownRight size={12} className="mt-0.5 shrink-0" aria-hidden="true" />
                  <span>
                    {keepOldAddress ? (
                      <>
                        L'ancienne adresse <span className="font-mono">/{page.slug}</span> sera redirigée
                        en 301 vers la nouvelle : les liens qui la visent continueront de fonctionner.
                      </>
                    ) : (
                      <>
                        L'ancienne adresse <span className="font-mono">/{page.slug}</span> ne mènera plus
                        nulle part : les liens qui la visent renverront une page introuvable.
                      </>
                    )}
                  </span>
                </p>
                <label className="mt-2 flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={keepOldAddress}
                    onChange={e => setKeepOldAddress(e.target.checked)}
                    className="mt-0.5 h-3.5 w-3.5 shrink-0 rounded border-gray-300 text-teal-700 focus:ring-2 focus:ring-teal-600"
                  />
                  <span className="font-medium">Rediriger l'ancienne adresse vers la nouvelle</span>
                </label>
              </div>
            )}
          </div>

          {/* Site */}
          <div>
            <label htmlFor="builder-site" className="block text-xs font-medium text-gray-500 mb-1.5">
              Site
            </label>
            {sitesLoading ? (
              <p className="text-xs text-gray-400 flex items-center gap-1.5">
                <Loader2 size={12} className="animate-spin" /> Chargement des sites…
              </p>
            ) : sitesError ? (
              <p className="text-xs text-red-600">Impossible de charger la liste des sites.</p>
            ) : (
              <>
                <select
                  id="builder-site"
                  value={siteId === null ? '' : String(siteId)}
                  onChange={e => setSiteId(e.target.value)}
                  aria-describedby="builder-site-help"
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-600"
                >
                  <option value="">Aucun site — page partagée</option>
                  {sites.map(s => (
                    <option key={s.id} value={String(s.id)}>{s.name}</option>
                  ))}
                </select>
                <p id="builder-site-help" className="text-xs text-gray-400 mt-1.5">
                  Le site détermine le domaine qui sert cette page. Sans site, elle reste
                  visible depuis tous les domaines.
                </p>
                {sites.length === 0 && (
                  <p className="text-xs text-gray-400 mt-1">Aucun site n'est encore déclaré.</p>
                )}
              </>
            )}
          </div>

          {/* Anciennes adresses — les chemins qui mènent encore ici. Comme l'URI
              et le site, c'est de la donnée de PAGE : rien n'est écrit depuis ce
              panneau, le Save du header persiste. Un <fieldset> parce que c'est
              un GROUPE de contrôles, pas un champ : sans lui, la liste et le
              champ d'ajout n'auraient aucun intitulé commun. */}
          <fieldset className="border-0 p-0 m-0">
            <legend className="text-xs font-medium text-gray-500 mb-1.5">
              Anciennes adresses
            </legend>
            {!page.pageNid ? (
              <p className="text-xs text-gray-400">
                Enregistrez la page pour lui ajouter des redirections.
              </p>
            ) : (
              <>
                {redirects.length > 0 && (
                  <ul className="space-y-1.5 mb-2">
                    {redirects.map(r => (
                      <li key={r.from} className="flex items-center gap-2">
                        <span className="flex-1 text-sm font-mono text-gray-700 truncate" title={r.from}>
                          {r.from}
                        </span>
                        {/* Le <select> a besoin d'un intitulé à lui : l'adresse
                            voisine n'en est pas un pour un lecteur d'écran. */}
                        <label className="sr-only" htmlFor={`builder-redirect-code-${r.from}`}>
                          Type de redirection pour {r.from}
                        </label>
                        <select
                          id={`builder-redirect-code-${r.from}`}
                          value={String(r.code)}
                          onChange={e => setRedirectCode(r.from, Number(e.target.value))}
                          className="px-2 py-1 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-600"
                        >
                          {REDIRECT_CODES.map(c => (
                            <option key={c.value} value={String(c.value)}>{c.label}</option>
                          ))}
                        </select>
                        <button
                          type="button"
                          onClick={() => removeRedirect(r.from)}
                          aria-label={`Retirer la redirection ${r.from}`}
                          className="p-1 rounded text-gray-400 hover:text-red-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-600 transition-colors"
                        >
                          <Trash2 size={14} aria-hidden="true" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}

                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-400 font-mono" aria-hidden="true">/</span>
                  <input
                    id="builder-redirect-new"
                    type="text"
                    value={newPath}
                    onChange={e => setNewPath(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addRedirect() } }}
                    aria-label="Ajouter une ancienne adresse"
                    aria-describedby="builder-redirect-help builder-redirect-status"
                    aria-invalid={pathCheck.state === 'ko'}
                    className={`flex-1 px-3 py-2 text-sm font-mono border rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-600 ${
                      pathCheck.state === 'ko' ? 'border-red-300' : 'border-gray-200'
                    }`}
                    placeholder="ancienne-adresse"
                  />
                  <button
                    type="button"
                    onClick={addRedirect}
                    disabled={pathCheck.state !== 'ok'}
                    className="inline-flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <Plus size={14} aria-hidden="true" /> Ajouter
                  </button>
                </div>
                <p id="builder-redirect-help" className="text-xs text-gray-400 mt-1.5">
                  Un visiteur qui demande cette adresse est renvoyé vers la page. Plusieurs
                  segments sont acceptés (<span className="font-mono">blog/vieil-article</span>).
                </p>
                {/* aria-live : le verdict arrive après la frappe, il doit être annoncé. */}
                <p id="builder-redirect-status" role="status" aria-live="polite" className="mt-1.5 text-xs">
                  {pathCheck.state === 'checking' && (
                    <span className="flex items-center gap-1.5 text-gray-400">
                      <Loader2 size={12} className="animate-spin" aria-hidden="true" /> Vérification…
                    </span>
                  )}
                  {pathCheck.state === 'ok' && (
                    <span className="flex items-center gap-1.5 text-teal-700">
                      <Check size={12} aria-hidden="true" /> Adresse disponible.
                    </span>
                  )}
                  {pathCheck.state === 'ko' && (
                    <span className="flex items-center gap-1.5 text-red-600">
                      <AlertCircle size={12} aria-hidden="true" /> {pathCheck.message || 'Adresse indisponible.'}
                    </span>
                  )}
                </p>
              </>
            )}
          </fieldset>

          {/* Gabarit : le drapeau ne se règle PLUS ici. Un gabarit naît comme
              tel (« Nouveau gabarit » depuis l'écran du site) ; une page n'en
              devient pas un en cochant une case — c'est ce qui mélangeait pages,
              gabarits et modèles de types de contenu. Le panneau le DIT
              seulement, parce que c'est ce qui explique l'URI ci-dessus. */}
          {isTemplate && (
            <p className="text-xs text-teal-800 bg-teal-50 border border-teal-100 rounded-lg px-3 py-2">
              Cette page est un <strong>gabarit</strong> : un point de départ pour créer
              des pages, servi nulle part. Pour qu'un header ou un footer reste
              <strong> commun</strong> à toutes les pages créées depuis lui, posez-le avec
              « Ajouter un bloc existant » : la duplication recopie alors la référence,
              pas le contenu.
            </p>
          )}
        </div>

        <footer className="px-6 py-4 border-t border-gray-100 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={handleApply}
            disabled={!canApply}
            className="px-3 py-1.5 rounded-lg text-sm font-medium bg-teal-700 text-white hover:bg-teal-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            Appliquer
          </button>
        </footer>
      </div>
    </div>,
    document.body,
  )
}

// Les codes qu'une redirection peut porter, dans les mots de l'éditeur. La
// liste doit rester alignée sur `REDIRECT_STATUS_CODES` côté Drupal, qui refuse
// tout autre code.
const REDIRECT_CODES = [
  { value: 301, label: 'Permanente (301)' },
  { value: 302, label: 'Temporaire (302)' },
  { value: 307, label: 'Temporaire, méthode conservée (307)' },
  { value: 308, label: 'Permanente, méthode conservée (308)' },
]

/**
 * La forme de référence d'un chemin, côté écran.
 *
 * Doublon ASSUMÉ de `OrbitBuilderController::normalizeRedirectPath()`, et
 * volontairement minimal : il ne sert qu'à comparer une saisie aux adresses
 * déjà posées dans le panneau, que le serveur ne connaît pas encore (rien
 * n'est enregistré avant le Save). Le VERDICT, lui, reste le sien.
 */
function normalizePath(value) {
  const path = '/' + String(value).trim().replace(/^\/+/, '').replace(/[?#].*$/, '')
  return (path.length > 1 ? path.replace(/\/+$/, '') : path).toLowerCase()
}

const URI_ERRORS = {
  format: 'Format invalide : minuscules, chiffres et tirets, et pas de tiret en tête.',
  reserved: "Cette URI est réservée à une section de l'application.",
  taken: 'Cette URI est déjà utilisée par une autre page.',
}

/**
 * Nettoie la saisie SANS la contrarier : on retire les accents et les
 * caractères interdits au fil de la frappe, mais on garde un tiret final
 * (sinon impossible de taper « mon-article »). Le verdict de forme reste celui
 * de Drupal.
 */
function sanitizeUri(value) {
  return value
    .toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9-]+/g, '-')
    .replace(/-{2,}/g, '-')
}

export default BuilderPageAddressPanel
